// frontend routes
const ROUTES = {
  root: '/',
  notFound: '*',
  errorPage: 'error',
  locations: 'locations',

  // Location-scoped top-level views
  yearOverview: 'locations/:locationId/year-overview',
  weekOverview: 'locations/:locationId/week-overview',
  economics: 'locations/:locationId/economics',
  patients: 'locations/:locationId/patients',

  // Location-scoped parents (settings & bookings live under the active location)
  locationSettings: 'locations/:locationId/settings',
  locationBookings: 'locations/:locationId/bookings',

  // Account-level (global) routes — no :locationId in the path
  configuration: 'configuration',
  treatmentPlanBuilder: 'configuration/treatment-plans/:templateId',
  configurationInterventions: 'configuration/interventions',
  settings: 'settings',

  // Relative child segments — settings (reused for both the global /settings/*
  // routes and the location-scoped /locations/:id/settings/* routes)
  settingsRooms: 'rooms',
  settingsInterventionRooms: 'intervention-rooms',
  settingsEmployees: 'employees',
  settingsRoomNeeds: 'room-needs',
  settingsInterventionRoomProperties: 'intervention-room-properties',
  settingsStaffCompetences: 'staff-competences',
  settingsUsers: 'users',

  // Relative child segments — bookings
  bookings: 'bookings',
  bookingsRoomBookings: 'room-bookings',
  bookingsStaffBookings: 'staff-bookings',
  bookingsClosedDays: 'closed-days',
  bookingsReservedStaffTime: 'reserved-staff-time',
  bookingsPatientBookings: 'patient-bookings'
} as const;

// Location-scoped page segments, for building links without repeating the
// path literals that also appear in the route patterns above.
export const LOCATION_SEGMENTS = {
  yearOverview: 'year-overview',
  weekOverview: 'week-overview',
  patients: 'patients',
  economics: 'economics'
} as const;

// The view a location selector / location card lands on by default.
export const DEFAULT_LOCATION_VIEW = LOCATION_SEGMENTS.yearOverview;

// Query param carrying the selected year on the year overview. The year lives
// in the URL so it survives page refresh and location switches.
export const YEAR_QUERY_PARAM = 'year';

// Settings subpages split by backend scope. Location settings are per-location
// (carry :locationId); global settings are account-wide and account-admin only.
export const LOCATION_SETTINGS_SEGMENTS = [
  ROUTES.settingsRooms,
  ROUTES.settingsInterventionRooms,
  ROUTES.settingsEmployees
] as const;

export const GLOBAL_SETTINGS_SEGMENTS = [
  ROUTES.settingsRoomNeeds,
  ROUTES.settingsInterventionRoomProperties,
  ROUTES.settingsStaffCompetences,
  ROUTES.settingsUsers
] as const;

export const BOOKINGS_SEGMENTS = [
  ROUTES.bookingsRoomBookings,
  ROUTES.bookingsStaffBookings,
  ROUTES.bookingsClosedDays,
  ROUTES.bookingsReservedStaffTime,
  ROUTES.bookingsPatientBookings
] as const;

// Builds an absolute path under a specific location, e.g.
// locationPath(id, 'settings', 'patients') -> /locations/<id>/settings/patients
export const locationPath = (locationId: string, ...segments: string[]): string =>
  `/locations/${encodeURIComponent(locationId)}${
    segments.length ? `/${segments.join('/')}` : ''
  }`;

export default ROUTES;
